import * as React from "react";
import { toast } from "react-toastify";
import StyledModal from "styled-react-modal";
import { useMappedState } from "redux-react-hook";
import { CellContext } from "@tanstack/react-table";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { ButtonLink } from "src/library/components/Link";
import { Modal } from "src/library/components/Modal";
import { ITableInstance } from "src/library/components/Tables/Table";
import { IAction } from "src/library/components/Button/ActionButton";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { getUserTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import { IStore } from "src/core/shared/store/index";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  ERPOdoo,
  ERPRJL,
  ERPSeedless,
  ERPUnity
} from "src/utils/constants";
import { Table } from "src/core/dashboard/components/widgets";

interface UsersPageProps {
  customerId: string;
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Customer "Users" View
 *
 */
const CustomerUsersPage: React.ReactFCWithChildren<PageProps<unknown, UsersPageProps>> = props => {
  const {
    history, match, terminology
  } = props;

  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const { isLoading: loadingCustomer, customer } = useCustomer(customerId);
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ usersResponse, getUsers ] = useUserApi("LIST_CUSTOMER_USERS");
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const [ exportCustomerUserRes, exportCustomerUsers ] = useUserApi("EXPORT_CUSTOMER_USER");
  const checkPermission = useCheckUserPermission();

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>}) => {
    getUsers({
      customerId,
      pagination: params?.pagination,
      filters: params?.filters
    });
  }, [ customerId, getUsers ]);

  React.useEffect(() => {
    if (exportCustomerUserRes.complete) {
      toast.success("Users are being exported. Check the job queue for details");
    }

    if (exportCustomerUserRes.error) {
      toast.error(`Error exporting users - ${exportCustomerUserRes.error.message}`);
    }
  }, [ exportCustomerUserRes ]);

  React.useEffect(() => {
    if (deleteUserResponse.data && TableRef.current) {
      TableRef.current.reloadTable();
    }

    if (deleteUserResponse.error) {
      toast("Could not delete this user!");
    }
  }, [ deleteUserResponse ]);

  const resendInviteUserAction = React.useCallback((id: number) => {
    resendInvite({ id });
  }, [ resendInvite ]);

  const editUserAction = React.useCallback((id: number) => {
    history.push(`/admin/customers/users/${id}/details`);
  }, [ history ]);

  const deleteUserAction = (id: number) => {
    setModalData(id);
  };

  const deleteUserActionConfirm = () => {
    if (modalData) {
      deleteUser({ id: modalData });
      setModalData(null);
    }
  };

  const handleSuspendUser = React.useCallback((user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  }, [ suspendUser ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ suspendUserResponse ]);

  const LikeToDoButtons = React.useCallback(() => {
    const buttons = [
      <ButtonLink
        key="new-user"
        to={`/admin/users/customers/${customerId}/create`}
      >
        {`Create new ${terminology.user.singular.text}`}
      </ButtonLink>,

      <Button
        key="export-customer-user"
        loading={exportCustomerUserRes.pending}
        onClick={() => {
          exportCustomerUsers({ customerIds: [ customerId ] });
        }}
      >
        {`Export ${terminology.user.plural.text}`}

      </Button>
    ];

    if (erp === ERPSeedless || erp === ERPRJL || erp === ERPUnity || erp === ERPOdoo) {
      buttons.push((
        <Button
          key="import-users"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.user.plural.text}`}
        </Button>)
      );
    }

    return buttons;
  }, [
    customerId,
    erp,
    exportCustomerUserRes.pending,
    exportCustomerUsers,
    terminology.user.plural.text,
    terminology.user.singular.text
  ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse, terminology.user.singular.text ]);

  const userTableActionsButton = React.useCallback((info: CellContext<IUser, unknown>) => {
    const items: IAction[] = [
      {
        title: "Edit",
        action: () => editUserAction(info.row.original.id),
        disabled: !checkPermission("amend-user")
      },
      {
        title: `${info.row.original.status === "active" ? "Suspend" : "Activate"}`,
        disabled: !checkPermission("amend-user"),
        action: () => {
          handleSuspendUser(info.row.original);
        }
      },
      {
        title: "Delete",
        disabled: !checkPermission("delete-user"),
        action: () => deleteUserAction(info.row.original.id)
      }
    ];

    if (info.row.original.lastLoggedIn === null) {
      items.push({
        title: "Resend invite",
        disabled: !checkPermission("create-user"),
        action: () => resendInviteUserAction(info.row.original.id)
      });
    }

    return (
      <ActionButton
        title="Actions"
        items={items}
      />
    );
  }, [
    checkPermission,
    editUserAction,
    handleSuspendUser,
    resendInviteUserAction
  ]);

  const columns = React.useMemo(() => {
    return getUserTableColumns({
      terminology,
      actionButton: userTableActionsButton,
      removeColumns: [ "defaultCustomer", "defaultDrop" ]
    });
  }, [ terminology, userTableActionsButton ]);

  return (
    <Layout
      {...props}
      title={customer ? `${customer.name} - ${terminology.user.plural.text}` : ""}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy={`Setup a new user for this ${terminology.customer.singular.text}.`}
            buttons={LikeToDoButtons()}
          />
        )}
      >

        <StyledModal isOpen={showImportModal}>
          <ImportModal
            open={showImportModal}
            onDismiss={() => setShowImportModal(false)}
            type="user"
            terminology={props.terminology}
          />
        </StyledModal>

        <Table
          ref={TableRef}
          mainAction={row => editUserAction(row.original.id)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          loading={usersResponse.pending}
          data={usersResponse.data?.result ? usersResponse.data?.result : []}
          onFetchData={onFetchData}
          rowCount={usersResponse?.data?.count ?? 0}
          pageCount={usersResponse.data?.pages ?? 0}
          defaultPagination={defaultPagination}
          columns={columns}
        />
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          isLoading={loadingCustomer}
          data={customer}
        />
      </InfoBarWrapper>

      <Modal
        small
        actions={[
          {
            name: "Yes",
            action: deleteUserActionConfirm,
            $buttonType: "primary"
          },
          {
            name: "No",
            action: () => setModalData(null),
            $buttonType: "secondary"
          }
        ]}
        backgroundAction={() => setModalData(null)}
        body={`Deleting this ${terminology.user.singular.text} cannot be undone`}
        type="confirm"
        title="Are you sure"
        show={modalData !== null}
      />
    </Layout>
  );
};

export default CustomerUsersPage;
